import { InitializationError } from '../components/ErrorPage/Errors';
import { tryOrNull } from '../utilities/tryOrNull';

function getEnvironmentVariableOrThrow(variableName: string): string {
	const variableValue: unknown = import.meta.env[variableName];

	if (!variableValue) {
		throw new InitializationError(`'${variableName}' is not defined`);
	}

	if (typeof variableValue !== 'string') {
		throw new InitializationError(`'${variableName}' is not a string`);
	}

	return variableValue;
}

function getEnvironmentVariableListOrThrow(variableName: string, separator = ','): ReadonlyArray<string> {
	const variableValue = getEnvironmentVariableOrThrow(variableName);
	return variableValue
		.split(separator)
		.map((item) => item.trim())
		.filter((item) => item.length > 0);
}

function getNameFormattedWithVitePrefix(variableName: string): string {
	return `VITE__${variableName}`;
}

const getViteEnvironmentVariableOrThrow = (variableName: string): string =>
	getEnvironmentVariableOrThrow(getNameFormattedWithVitePrefix(variableName));

const getViteEnvironmentVariableOrNull = tryOrNull(getViteEnvironmentVariableOrThrow);

const getViteEnvironmentVariableListOrThrow = (variableName: string, separator = ','): ReadonlyArray<string> =>
	getEnvironmentVariableListOrThrow(getNameFormattedWithVitePrefix(variableName), separator);

const VITE_APP_STAGE = getViteEnvironmentVariableOrThrow('STAGE');

export const ENVIRONMENT_NAME = VITE_APP_STAGE;

const PRODUCTION_ENVIRONMENT_NAME = 'production';
export const IS_PRODUCTION = ENVIRONMENT_NAME === PRODUCTION_ENVIRONMENT_NAME;

export const COGNITO_USER_POOL_ID = getViteEnvironmentVariableOrThrow('COGNITO_USER_POOL_ID');
export const COGNITO_APP_CLIENT_ID = getViteEnvironmentVariableOrThrow('COGNITO_APP_CLIENT_ID');
export const COGNITO_COOKIE_DOMAIN =
	getViteEnvironmentVariableOrNull('COGNITO_COOKIE_DOMAIN') ?? globalThis.location.hostname;

export const MIXPANEL_KEY = getViteEnvironmentVariableOrThrow('MIXPANEL_KEY');
export const MIXPANEL_HOST_LIST = getViteEnvironmentVariableListOrThrow('MIXPANEL_HOST_LIST');
export const MIXPANEL_ENABLED = getViteEnvironmentVariableOrThrow('MIXPANEL_ENABLED') === 'true';

export const APPCUES_ACCOUNT_ID = getViteEnvironmentVariableOrThrow('APPCUES_ACCOUNT_ID');
export const APPCUES_ENABLED = getViteEnvironmentVariableOrThrow('APPCUES_ENABLED') === 'true';

export const EPIC_2ND_LOGIN_FLOW_ENABLED =
	(getViteEnvironmentVariableOrNull('EPIC_2ND_LOGIN_FLOW_ENABLED') ?? 'true') === 'true';

export const AUTH0_LOGIN_REDIRECT_BASE_URL = getViteEnvironmentVariableOrThrow('AUTH0_LOGIN_REDIRECT_BASE_URL');

export const AUTH0_LOGIN_REDIRECT = `${globalThis.location.origin}/login/`;
export const AUTH0_CLIENT_ID = getViteEnvironmentVariableOrThrow('AUTH0_CLIENT_ID');

export const LAUNCH_DARKLY_KEY = getViteEnvironmentVariableOrThrow('LAUNCH_DARKLY_KEY');

export const LOGROCKET_ID = getViteEnvironmentVariableOrThrow('LOGROCKET_ID');

export const DATADOG_CLIENT_TOKEN = getViteEnvironmentVariableOrNull('DATADOG_CLIENT_TOKEN');
export const DATADOG_APPLICATION_ID = getViteEnvironmentVariableOrNull('DATADOG_APPLICATION_ID');

export const API_URL_SUMMARY_LAMBDAS = getViteEnvironmentVariableOrThrow('API_URL_SUMMARY_LAMBDAS');
export const API_URL_AUTO_DOCUMENTATION = getViteEnvironmentVariableOrThrow('API_URL_AUTO_DOCUMENTATION');
export const API_URL_DX_EVENTS = getViteEnvironmentVariableOrThrow('API_URL_DX_EVENTS');
export const API_URL_DX_SUGGESTIONS = getViteEnvironmentVariableOrThrow('API_URL_DX_SUGGESTIONS');
export const API_URL_DX_EHR_GATEWAY = getViteEnvironmentVariableOrThrow('API_URL_DX_EHR_GATEWAY');
export const API_URL_SCHEDULE_SERVICE = getViteEnvironmentVariableOrThrow('API_URL_SCHEDULE_SERVICE');
export const API_URL_PATIENT_PORTRAIT = getViteEnvironmentVariableOrThrow('API_URL_PATIENT_PORTRAIT');
export const API_URL_QUALITY = getViteEnvironmentVariableOrThrow('API_URL_QUALITY');
export const API_URL_ADMIN_TOOL = getViteEnvironmentVariableOrThrow('API_URL_ADMIN_TOOL');
export const API_URL_AMBIENT = getViteEnvironmentVariableOrThrow('API_URL_AMBIENT');
export const API_URL_ICD_DATA = getViteEnvironmentVariableOrThrow('API_URL_ICD_DATA');
export const DX_WEBSOCKET_API = getViteEnvironmentVariableOrThrow('DX_WEBSOCKET_API');
export const API_URL_FILE_MANAGER = getViteEnvironmentVariableOrThrow('API_URL_FILE_MANAGER');

export const FAVICON_COLOR_OVERRIDE = getViteEnvironmentVariableOrNull('FAVICON_COLOR_OVERRIDE');

export const DISABLE_IDLE_OBSERVER_VALUE = -1;
export const SESSION_TIMEOUT_IN_SECONDS = Number(
	getViteEnvironmentVariableOrNull('SESSION_INACTIVITY_TIMEOUT_SECONDS') ?? DISABLE_IDLE_OBSERVER_VALUE,
);

export const IDLE_OBSERVER_INTERVAL_IN_SECONDS = Number(
	getViteEnvironmentVariableOrNull('IDLE_OBSERVER_INTERVAL_IN_SECONDS') ?? 10,
);

export const IDLE_OBSERVER_NOTIFICATION_DURATION_IN_SECONDS = Number(
	getViteEnvironmentVariableOrNull('IDLE_OBSERVER_NOTIFICATION_DURATION_IN_SECONDS') ?? 3,
);

export const IDLE_OBSERVER_NOTIFICATION_MESSAGE =
	getViteEnvironmentVariableOrNull('IDLE_OBSERVER_NOTIFICATION_MESSAGE') ??
	'Session ended due to inactivity. You will be logged out.';

// From Auth0 documentation: https://auth0.com/docs/get-started/authentication-and-authorization-flow/implicit-flow-with-form-post/mitigate-replay-attacks-when-using-the-implicit-flow#generate-a-cryptographically-random-nonce
function generateRandomString(length: number): string {
	const charset = '0123456789ABCDEFGHIJKLMNOPQRSTUVXYZabcdefghijklmnopqrstuvwxyz+/';
	let result = '';
	let remainingLength = length; // Create a separate variable for the loop condition

	while (remainingLength > 0) {
		const byteArray = new Uint8Array(16);
		const randomValues = globalThis.crypto.getRandomValues(byteArray);

		for (let i = 0; i < randomValues.length && remainingLength > 0; i++) {
			const randomValue = randomValues[i] ?? 0;

			if (randomValue < charset.length) {
				result += charset[randomValue];
				remainingLength--;
			}
		}
	}

	return result;
}

let maybeAuth0Nonce: ReturnType<typeof generateRandomString> | null = null;
function getOrCreateAuth0Nonce(): string {
	maybeAuth0Nonce ??= generateRandomString(16);
	return maybeAuth0Nonce;
}

export function getNavinaAuth0AuthURL(redirectUri: string = AUTH0_LOGIN_REDIRECT): string {
	const auth0Nonce = getOrCreateAuth0Nonce();
	return `${AUTH0_LOGIN_REDIRECT_BASE_URL}authorize?response_type=id_token%20token&client_id=${AUTH0_CLIENT_ID}&prompt=none&redirect_uri=${redirectUri}&nonce=${auth0Nonce}&scope=offline_access`;
}

export const NEW_DOCUMENT_VIEWER_EXPERIENCE_FLAG = 'flag_CoreServices_DocumentViewerExperience';
export const NEW_DOCUMENT_VIEWER_EXPERIENCE_FLAG_DEFAULT_VALUE = false;
